import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus } from "lucide-react";

/* Generic register: fields = [{ key, label, type: text|textarea|date|select|datetime, options, table }] */
export const GovRegister = ({ endpoint, fields, title, intro, testId, newLabel = "Add" }) => {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const load = () => api.get(`/governance/${endpoint}`).then(({ data }) => setRows(data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [endpoint]);

  const save = async () => {
    setBusy(true);
    const body = {}; fields.forEach((f) => { body[f.key] = edit[f.key] ?? ""; });
    try { edit.id ? await api.put(`/governance/${endpoint}/${edit.id}`, body) : await api.post(`/governance/${endpoint}`, body); toast.success("Saved"); setEdit(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const cols = fields.filter((f) => f.table);
  const shown = rows.filter((r) => !filter || JSON.stringify(r).toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="space-y-3" data-testid={testId}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-3xl">{intro}</p>
        <div className="flex gap-2">
          <Input placeholder="Filter…" value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 w-40 text-xs" data-testid={`${testId}-filter`} />
          <Button size="sm" className="gap-1" onClick={() => setEdit(Object.fromEntries(fields.map((f) => [f.key, f.default ?? ""])))} data-testid={`${testId}-add-btn`}><Plus size={13} /> {newLabel}</Button>
        </div>
      </div>
      <div className="bg-white border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-muted-foreground"><tr>{cols.map((c) => <th key={c.key} className="px-3 py-2 text-left font-medium">{c.label}</th>)}</tr></thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={cols.length} className="px-3 py-6 text-center text-muted-foreground">Nothing recorded yet.</td></tr>}
            {shown.map((r) => (
              <tr key={r.id} onClick={() => setEdit(r)} className="border-t hover:bg-slate-50 cursor-pointer align-top" data-testid={`${testId}-row-${r.id}`}>
                {cols.map((c) => <td key={c.key} className="px-3 py-2 text-xs max-w-xs">{String(r[c.key] ?? "")}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader><DialogTitle>{edit?.id ? `Edit ${title}` : `New ${title}`}</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid md:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
                  <Label className="text-xs">{f.label}</Label>
                  {f.type === "textarea" ? <Textarea rows={3} value={edit[f.key] || ""} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })} data-testid={`${testId}-field-${f.key}`} />
                    : f.type === "select" ? (
                      <select value={edit[f.key] || ""} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })} className="h-9 w-full rounded-md border border-border bg-white px-2 text-sm" data-testid={`${testId}-field-${f.key}`}>
                        <option value="">—</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>)
                    : <Input type={f.type === "date" ? "date" : f.type === "datetime" ? "datetime-local" : "text"} value={edit[f.key] || ""} onChange={(e) => setEdit({ ...edit, [f.key]: e.target.value })} data-testid={`${testId}-field-${f.key}`} />}
                </div>
              ))}
              {edit.history?.length > 0 && (
                <div className="md:col-span-2 text-[11px] text-muted-foreground"><p className="font-semibold text-slate-700">History</p>
                  {[...edit.history].reverse().slice(0, 10).map((h, i) => <p key={i}>{new Date(h.at).toLocaleString("en-GB")} · {h.action} · {h.by}{h.fields?.length ? ` · ${h.fields.join(", ")}` : ""}</p>)}</div>
              )}
              <div className="md:col-span-2 flex gap-2"><Button disabled={busy} onClick={save} data-testid={`${testId}-save-btn`}>Save</Button><Button variant="ghost" onClick={() => setEdit(null)}>Cancel</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
